import { useFormik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Card,
  CardBody,
  Form,
  Progress,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";
import SelectSearchFile from "./SelectSearchFile";
import {
  bulkSearch,
  downloadBulkSearchTemplate,
} from "common/services/search.service";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import { Link } from "react-router-dom";

const BulkSearch = () => {
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [resultsUrl, setResultsUrl] = useState("");
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [progress, setProgress] = useState(10);
  const [timeRemaining, setTimeRemaining] = useState("...");

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      selectedFiles: [],
    },
    validationSchema: Yup.object({
      selectedFiles: Yup.array()
        .min(1, "At least one file is required")
        .required("Required"),
    }),
    onSubmit: async (values: any) => {
      const formData = new FormData();

      values.selectedFiles.forEach((file: File) => {
        formData.append("files", file, file.name);
      });
      formData.append("data", JSON.stringify({}));

      try {
        setLoading(true);
        setSubmitted(true);
        setError("");

        const response: any = await bulkSearch(formData);
        setResultsUrl(response?.xlsx?.url || "");
        setProgress(100);
        setTimeRemaining("Processed");
      } catch (bulkSearchError) {
        toast.error("An error occurred while processing the bulk search");
        setError("An error occurred while processing the bulk search");
      } finally {
        setLoading(false);
      }
    },
  });

  const startNewSearch = useCallback(() => {
    setSubmitted(false);
    validation.resetForm();
    setProgress(10);
    setTimeRemaining("...");
    setResultsUrl("");
    setError("");
  }, [validation]);

  const downloadTemplate = useCallback(async () => {
    setDownloading(true);

    try {
      const response: any = await downloadBulkSearchTemplate();
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Bulk_Search_Template.csv");
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (downloadError) {
      toast.error("An error occurred while downloading the bulk search template");
    } finally {
      setDownloading(false);
    }
  }, []);

  useEffect(() => {
    let progressInterval: ReturnType<typeof setInterval> | undefined;

    if (progress < 50 && loading) {
      progressInterval = setInterval(() => {
        setProgress((currentProgress) => Math.min(currentProgress + 5, 50));
      }, 1000);
    } else if (progress < 70 && loading) {
      progressInterval = setInterval(() => {
        setProgress((currentProgress) => Math.min(currentProgress + 3, 70));
      }, 3000);
    } else if (progress < 85 && loading) {
      progressInterval = setInterval(() => {
        setProgress((currentProgress) => Math.min(currentProgress + 2, 85));
      }, 3000);
    }

    return () => {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
    };
  }, [progress, loading]);

  return (
    <Card>
      <CardBody>
        {submitted ? (
          <>
            <Card className="bg-light overflow-hidden shadow-none">
              <CardBody>
                <div className="d-flex">
                  <div className="flex-grow-1">
                    <h6 className="mb-0">
                      <b
                        className={error ? "text-danger" : "text-secondary"}
                      >
                        {progress}%
                      </b>{" "}
                      {loading
                        ? "Searching..."
                        : error
                        ? "Failed"
                        : "Completed!"}
                    </h6>
                  </div>
                  <div className="flex-shrink-0">
                    <h6 className="mb-0">
                      {error ? "Failed" : timeRemaining}
                    </h6>
                  </div>
                </div>
              </CardBody>
              <Progress
                value={progress}
                color={error ? "danger" : "primary"}
                className="bg-info-subtle rounded-0"
              />
            </Card>

            {loading ? (
              <Skeleton
                height={200}
                baseColor="#F1F4F7"
                highlightColor="#E3E8ED"
              />
            ) : error ? (
              <Alert color="danger" className="material-shadow">
                <strong>
                  An error occurred while processing the bulk search.{" "}
                </strong>
                Please try again later or contact the Risk Management Unit if
                the issue persists.
              </Alert>
            ) : (
              <Alert color="primary" className="material-shadow">
                <strong>
                  Good news! Your bulk search has been successfully completed.
                </strong>{" "}
                You can now download the results in Excel format.
              </Alert>
            )}

            <div className="d-flex justify-content-between pt-3">
              <button
                type="button"
                className="btn btn-info"
                onClick={startNewSearch}
                disabled={loading}
              >
                <i className="ri-restart-line me-1"></i>
                New Bulk Search
              </button>

              {!loading && !error && (
                <Link
                  className="btn btn-primary"
                  to={resultsUrl}
                  target="_blank"
                >
                  <i className="ri-download-2-line me-1"></i>
                  Download Results
                </Link>
              )}
            </div>
          </>
        ) : (
          <Form
            className="tablelist-form"
            onSubmit={(event) => {
              event.preventDefault();
              validation.handleSubmit();
            }}
          >
            <SelectSearchFile validation={validation} />

            <div className="d-flex justify-content-between pt-4">
              <button
                type="button"
                className="btn btn-light"
                onClick={downloadTemplate}
                disabled={downloading}
              >
                {downloading && (
                  <Spinner color="light" size="sm" className="me-2" />
                )}
                <i className="ri-file-download-line me-1"></i>
                Download Template
              </button>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={
                  loading || validation.values.selectedFiles.length === 0
                }
              >
                {loading && <Spinner color="light" size="sm" className="me-2" />}
                <i className="ri-search-line me-1"></i>
                Search
              </button>
            </div>
          </Form>
        )}
      </CardBody>
    </Card>
  );
};

export default BulkSearch;
